import {
    type CollectionReference,
    doc,
    type DocumentReference,
    orderBy,
    type Query,
    query,
    where,
} from 'firebase/firestore';

import DocumentCollection, {
    type DocumentStoreClass,
} from './DocumentCollection';
import type DocumentFolder from './DocumentFolder';
import type DocumentStore from './DocumentStore';

export default class DocumentFolderCollection<
    ChildType extends DocumentStore<any> = any,
    ChildSchema = any,
> extends DocumentCollection<ChildType, ChildSchema> {
    query = (): Query | null => {
        return query(
            this.collectionRef,
            where('archived', '==', false),
            orderBy('dateModified', 'desc')
        );
    };

    get tags() {
        const allTags = this.all.map((p) => p.data.tags).flat() as string[];
        const uniqueTags = [...new Set(allTags)];
        return uniqueTags;
    }

    addFolder = async <T extends DocumentFolder<any, any>>(
        itemClass: DocumentStoreClass<T>,
        data: Partial<T['schema']>
    ): Promise<T> => {
        const docRef: DocumentReference = doc(this.collectionRef);
        const newChild = new itemClass(this, docRef);
        newChild._update({ ...newChild.getDefaults(), ...data });
        newChild.onReady();
        await Promise.all([
            // Bumps the parent's sort position; not worth failing the
            // creation over.
            this.parent?.updateDateModified().catch((e: unknown) => {
                console.error(
                    '[DocumentFolderCollection.addFolder] parent.updateDateModified failed',
                    e
                );
            }),
            newChild.save(),
        ]);
        this.children.push(newChild as any);

        return newChild;
    };

    constructor(
        parent: DocumentStore<any, any> | CollectionReference,
        childClass: DocumentStoreClass<ChildType>
    ) {
        super(parent, childClass);
        this.becomeObservable();
    }
}
